import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { axiosInstance } from '../services/axios-instance';
import { OrdersService } from '../services/orders-service/service';
import { errorMessage, ObjectUtils, parseAxiosError } from '../utils';
import {
  Order,
  OrderList,
  OrderStatus,
  TestKit,
} from '../services/orders-service/types';
import { PaginateOptions, StateLoading } from '../services/types';
import { useAuthStore } from './auth-store';
import { LabResult } from '../services/lab-results-service/types';

interface OrderState {
  orders: Order[];
  nextPage?: number;
  prevPage?: number;
  currentOrder: Order | null;
  currentPage: number;
  totalPages: number;
  serverError: string;
  loadingOrders: boolean;
  loadingResults: boolean;
  count: number;
  totalCount: number;
  updateOrders: Record<number, StateLoading<Order>>;
  resultPDF: Record<string | number, StateLoading<string>>;
  deleteOrderStatus: StateLoading<number>;
}

interface OrderAction {
  listOrders: { (options: PaginateOptions): Promise<OrderList> };
  getDetailedOrder: { (id: number): Promise<Order> };
  clearCurrentOrder: { (): void };
  updateStatus: (
    order: Partial<Order> & { id: number },
    status: OrderStatus,
  ) => Promise<void>;
  updateDetails: (order: Partial<Order> & { id: number }) => Promise<void>;
  fetchResultPDF: (labResult: LabResult) => Promise<void>;
  deleteOrder: (params: { id: number }) => Promise<void>;
}

const initialState = {
  orders: [] as Order[],
  nextPage: undefined,
  prevPage: undefined,
  currentOrder: null,
  loadingResults: false,
  currentPage: 1,
  totalPages: 1,
  count: 0,
  totalCount: 0,
  loadingOrders: false,
  serverError: '',
  updateOrders: {},
  resultPDF: {},
  deleteOrderStatus: {},
};

const orderService = new OrdersService(axiosInstance);

export const useOrderStore = create<OrderState & OrderAction>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,
        deleteOrder: async ({ id }) => {
          set({ deleteOrderStatus: { isLoading: true } });
          try {
            await axiosInstance({
              method: 'DELETE',
              url: `/orders/${id}`,
            });
            set({
              deleteOrderStatus: { isLoading: false },
            });
          } catch (err) {
            set({
              deleteOrderStatus: { isLoading: false, error: errorMessage(err) },
            });
          }
        },
        listOrders: async options => {
          set(() => ({ loadingOrders: true }));
          let data: OrderList;
          try {
            data = await orderService.list(options);
          } catch (error) {
            set(() => ({
              serverError: parseAxiosError(error),
              loadingOrders: false,
            }));
            throw error;
          }
          set(() => ({
            orders: data.items.map((order, index) => ({
              ...order,
              id: index + 1,
              orderId: order.id,
            })),
            nextPage: data.nextPage,
            prevPage: data.prevPage,
            currentPage: Number(data.page),
            totalPages: Number(data.totalPages),
            count: data.itemCount,
            totalCount: data.totalItems,
            loadingOrders: false,
            serverError: '',
          }));
          return data;
        },
        clearCurrentOrder: () => set(() => ({ currentOrder: null })),
        getDetailedOrder: async id => {
          const order = await orderService.getById({ id });
          set(() => ({ currentOrder: order }));
          return order;
        },
        updateStatus: async (order, status) => {
          const body = ObjectUtils.filterEmpty(order);
          const update = (data: StateLoading<Order>) => {
            set(state => {
              const updateOrders = { ...(state.updateOrders || {}) };
              const cData = { ...updateOrders[order.id] };
              updateOrders[order.id] = { ...cData, ...data };
              return { updateOrders };
            });
          };
          update({ isLoading: true });
          try {
            if (!order.testKit) {
              throw new Error("This order doesn't have test kit.");
            }
            const testKit: TestKit = order.testKit as TestKit;
            let url = '/test-kits';
            switch (status) {
              case OrderStatus.ORDER_APPROVED:
                url = `/test-kits/${testKit?.id || ''}/order-approve`;
                break;
              case OrderStatus.ORDER_REJECTED:
                url = `/test-kits/${testKit?.id || ''}/order-reject`;
                break;
              case OrderStatus.KIT_SHIPPED:
                url = `/test-kits/${testKit?.id || ''}/kit-shipped`;
                break;
              case OrderStatus.SPECIMEN_RECEIVED:
                url = `/test-kits/${testKit?.id || ''}/specimen-received`;
                break;
              case OrderStatus.RECOLLECTION_REQUIRED:
                url = `/test-kits/${testKit?.id || ''}/recollection-required`;
                break;
              case OrderStatus.COMPLETED:
                url = `/test-kits/${testKit?.id || ''}/completed`;
                break;
              case OrderStatus.RELEASE_APPROVED:
                url = `/test-kits/${testKit?.id || ''}/release-approve`;
                break;
              case OrderStatus.RELEASE_REJECTED:
                url = `/test-kits/${testKit?.id || ''}/release-reject`;
                break;
              default:
                break;
            }
            await axiosInstance({
              url,
              method: 'POST',
              data: body,
            });
            set(state => {
              const orders = state.orders || [];
              const index = orders.findIndex(cOrder => cOrder.id === order.id);
              if (index >= 0) {
                const cOrder = orders[index];
                cOrder.status = status;
                orders[index] = cOrder;
              }
              return { orders };
            });

            const currentOrder: Order = order as Order;
            currentOrder.status = status;
            if (currentOrder.testKit) {
              const cUser = useAuthStore.getState().idToken.payload;
              currentOrder.testKit.approvingPhysicianFirstName =
                cUser.given_name;
              currentOrder.testKit.approvingPhysicianLastName =
                cUser.family_name;
            }
            set({ currentOrder });
            update({ isLoading: false, data: currentOrder });
          } catch (err) {
            console.log(err);
            update({ isLoading: false, error: errorMessage(err) });
          }
        },
        updateDetails: async order => {
          const body = ObjectUtils.filterEmpty(order);
          const update = (data: StateLoading<Order>) => {
            set(state => {
              const updateOrders = { ...(state.updateOrders || {}) };
              const cData = { ...updateOrders[order.id] };
              updateOrders[order.id] = { ...cData, ...data };
              return { updateOrders };
            });
          };
          update({ isLoading: true });
          try {
            if (!order.testKit) {
              throw new Error("This order doesn't have test kit.");
            }
            const testKit: TestKit = order.testKit as TestKit;
            const url = `/test-kits/${testKit?.id || ''}/submit-order-details`;
            await axiosInstance({
              url,
              method: 'POST',
              data: body,
            });
            set(state => {
              const orders = state.orders || [];
              const index = orders.findIndex(cOrder => cOrder.id === order.id);
              if (index >= 0) {
                const cOrder = orders[index];
                orders[index] = cOrder;
              }
              return { orders };
            });

            const currentOrder: Order = order as Order;
            await get().getDetailedOrder(Number(order.id));
            update({ isLoading: false, data: currentOrder });
          } catch (err) {
            update({ isLoading: false, error: errorMessage(err) });
            throw err;
          }
        },
        fetchResultPDF: async labResult => {
          const updateState = (data: Partial<StateLoading<string>>) => {
            set(({ resultPDF = {} }) => {
              const cData = resultPDF[`${labResult.id}`] || {};
              // eslint-disable-next-line no-param-reassign
              resultPDF[`${labResult.id}`] = { ...cData, ...data };
              return { resultPDF: { ...resultPDF } };
            });
          };
          updateState({ isLoading: true, error: undefined });
          try {
            const { data } = await axiosInstance(
              `/lab-results/view/${labResult.id}`,
            );
            updateState({ data: data as string, isLoading: false });
          } catch (err) {
            updateState({ error: errorMessage(err), isLoading: false });
            throw err;
          }
        },
      }),
      { name: 'order-storage' },
    ),
  ),
);
